import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { environment } from '../config/environment';
import './Career.css';

// ===== DUMMY / INITIAL JOBS ARRAY (Easy for API Integration) =====
export const initialDummyJobs = [
  {
    id: 1,
    title: 'Data Entry Operator (DEO)',
    department: 'IT & Digitization Wing',
    type: 'Full Time',
    location: 'Patna / Regional Offices',
    openings: 15,
    experience: '0 - 2 Years',
    salary: '₹14,000 - ₹22,000 / mo',
    skills: ['MS Office', 'Typing 30+ WPM', 'Excel', 'Data Scanning'],
    isLive: true,
    tag: 'Urgent Requirement',
  },
  {
    id: 2,
    title: 'Multi-Tasking Staff (MTS)',
    department: 'Facility & Office Operations',
    type: 'Full Time',
    location: 'Government & Corporate Sites',
    openings: 25,
    experience: 'Fresher / 1 Year',
    salary: '₹12,000 - ₹18,000 / mo',
    skills: ['File Handling', 'Office Coordination', 'Store Maintenance'],
    isLive: true,
    tag: 'Walk-in Active',
  },
  {
    id: 3,
    title: 'Primary Teacher (PRT)',
    department: 'Educational & ICT Support',
    type: 'Full Time',
    location: 'Affiliated Schools / Centres',
    openings: 10,
    experience: '1 - 3 Years',
    salary: '₹18,000 - ₹28,000 / mo',
    skills: ['D.El.Ed / B.Ed', 'Basic Computer Skills', 'Classroom Management'],
    isLive: true,
    tag: 'Featured',
  },
  {
    id: 4,
    title: 'ICT Lab Computer Instructor',
    department: 'IT & Digitization Wing',
    type: 'Full Time',
    location: 'District ICT School Labs',
    openings: 8,
    experience: '1 - 3 Years',
    salary: '₹16,000 - ₹25,000 / mo',
    skills: ['BCA / PGDCA', 'Hardware Troubleshooting', 'Internet & MS Office'],
    isLive: true,
    tag: 'New Opening',
  },
  {
    id: 5,
    title: 'Security Guard & Supervisor',
    department: 'Security & Intelligence Wing',
    type: 'Full Time (Rotational)',
    location: 'Corporate & Society Sites',
    openings: 40,
    experience: '0 - 4 Years',
    salary: '₹15,000 - ₹24,000 / mo',
    skills: ['Physical Fitness', 'Access Registry', 'Fire Safety Basics'],
    isLive: true,
    tag: 'Mass Hiring',
  },
  {
    id: 6,
    title: 'Housekeeping Supervisor',
    department: 'Facility & Housekeeping Wing',
    type: 'Full Time',
    location: 'Commercial Complexes',
    openings: 12,
    experience: '1 - 2 Years',
    salary: '₹14,000 - ₹20,000 / mo',
    skills: ['Machine Scrubbing', 'Chemical Safety', 'Team Supervision'],
    isLive: true,
    tag: 'Immediate Joining',
  },
];

const Career = () => {
  const navigate = useNavigate();
  const [jobOpenings, setJobOpenings] = useState(initialDummyJobs);
  const [activeFilter, setActiveFilter] = useState('All');

  // Attempt backend API fetch; if empty or fails, keep the structured dummy jobs array
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const response = await fetch(`${environment.baseUrl}${environment.endpoints.jobApplicationGetAll}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });

        const data = await response.json();

        if (response.ok && data?.response === true && Array.isArray(data?.data) && data.data.length > 0) {
          const apiJobs = data.data
            .filter(item => String(item.status) === '1')
            .map((item, index) => ({
              id: item.id || index + 1,
              title: item.title || 'Untitled Position',
              department: item.department || 'Operations',
              type: item.type || 'Full Time',
              location: item.location || 'Multiple Locations',
              openings: item.openings || 5,
              experience: item.experience || '0 - 2 Years',
              salary: item.salary || 'Best in Industry',
              skills: item.skills ? (Array.isArray(item.skills) ? item.skills : item.skills.split(',')) : ['Discipline', 'Communication'],
              isLive: true,
              tag: 'Active',
            }));

          if (apiJobs.length > 0) {
            setJobOpenings(apiJobs);
          }
        }
      } catch (err) {
        // Fallback already defaults to initialDummyJobs
      }
    };

    fetchJobs();
  }, []);

  const filterCategories = ['All', 'IT & Digitization Wing', 'Facility & Office Operations', 'Educational & ICT Support', 'Security & Intelligence Wing'];

  const filteredJobs = activeFilter === 'All'
    ? jobOpenings
    : jobOpenings.filter(j => j.department.toLowerCase().includes(activeFilter.toLowerCase()) || j.department === activeFilter);

  return (
    <section id="career" className="career-page">
      <div className="career-unified-container">

        {/* ===== UNIFIED CAREER HEADER & METRICS ===== */}
        <motion.div
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="career-unified-header"
        >
          <div className="career-badge-group">
            <span className="career-hero-badge">💼 Career Opportunities</span>
            <span className="career-live-pulse-badge">
              <span className="live-pulse-dot" />
              {jobOpenings.length} Positions Available
            </span>
          </div>

          <h2 className="career-main-heading">
            Current Active Vacancies at <span className="text-[#1976D2] italic font-serif font-semibold">AVISS</span>
          </h2>
          <div className="career-heading-divider" />
          <p className="career-main-subtext">
            Explore verified career openings across state government projects, corporate sites, ICT school labs, and facility operations.
          </p>

          {/* Quick Metrics Bar */}
          <div className="career-metrics-row">
            <div className="career-metric-card">
              <span className="metric-val">{jobOpenings.length}+</span>
              <span className="metric-lbl">Live Roles</span>
            </div>
            <div className="career-metric-card">
              <span className="metric-val">100+</span>
              <span className="metric-lbl">Total Vacancies</span>
            </div>
            <div className="career-metric-card">
              <span className="metric-val">100%</span>
              <span className="metric-lbl">Statutory Compliance (PF/ESIC)</span>
            </div>
            <div className="career-metric-card">
              <span className="metric-val">07th</span>
              <span className="metric-lbl">Monthly Guaranteed Payout</span>
            </div>
          </div>
        </motion.div>

        {/* ===== CONTROLS ROW: Filters & View Portal CTA ===== */}
        <div className="career-controls-bar">
          <div className="career-filter-tabs">
            {filterCategories.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveFilter(cat)}
                className={`career-filter-pill ${activeFilter === cat ? 'active' : ''}`}
              >
                {cat}
              </button>
            ))}
          </div>

          <button
            type="button"
            className="career-view-all-btn"
            style={{ cursor: 'default', opacity: 0.9 }}
          >
            View Full Portal →
          </button>
        </div>

        {/* Cards Grid */}
        <div className="career-cards-grid">
          {filteredJobs.map((job, idx) => (
            <motion.div
              key={job.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: idx * 0.08 }}
              whileHover={{ y: -6 }}
              className="career-job-card"
            >
              <div className="job-card-top">
                <span className="job-tag-chip">{job.tag}</span>
                <span className="job-live-indicator">
                  <span className="indicator-dot" /> Live
                </span>
              </div>

              <h3 className="job-card-title">{job.title}</h3>
              <p className="job-card-dept">{job.department}</p>

              <div className="job-meta-grid">
                <div className="job-meta-item">
                  <span className="meta-icon">📍</span>
                  <span className="meta-val">{job.location}</span>
                </div>
                <div className="job-meta-item">
                  <span className="meta-icon">👥</span>
                  <span className="meta-val">{job.openings} Openings</span>
                </div>
                <div className="job-meta-item">
                  <span className="meta-icon">⏱️</span>
                  <span className="meta-val">{job.experience}</span>
                </div>
                <div className="job-meta-item">
                  <span className="meta-icon">💰</span>
                  <span className="meta-val">{job.salary}</span>
                </div>
              </div>

              {job.skills && (
                <div className="job-skills-wrap">
                  {job.skills.map((s, i) => (
                    <span key={i} className="skill-pill">{s}</span>
                  ))}
                </div>
              )}

              <div className="job-card-footer">
                <button
                  type="button"
                  className="job-apply-btn"
                  style={{ cursor: 'default' }}
                >
                  Apply Now →
                </button>
                <button
                  type="button"
                  className="job-details-link"
                  style={{ cursor: 'default' }}
                >
                  Details
                </button>
              </div>
            </motion.div>
          ))}
        </div>

      </div>

      {/* ===== WHY CHOOSE AVISS - 3D MODERN CARDS ===== */}
      <section className="career-why-join">
        <div className="career-why-join-container">
          <span className="career-hero-badge">Why Partner With Us</span>
          <h2>
            Why Choose a Career With <span className="text-[#1976D2] italic font-serif font-semibold">AVISS?</span>
          </h2>
          <div className="career-heading-divider" />
          <p className="career-why-subtitle">
            We empower every candidate with verified corporate placement, transparent promotions, and dedicated welfare support.
          </p>

          <div className="career-why-grid">
            {/* Card 1: Fast Progression */}
            <div className="career-why-card 3d-card">
              <div className="why-card-glow" />
              <div className="career-why-icon-3d icon-progression">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
                  <polyline points="17 6 23 6 23 12" />
                </svg>
              </div>
              <span className="why-card-badge">Merit Based</span>
              <h4>Career Progression</h4>
              <p>Transparent promotion tracks with periodic skill enhancement, ICT certifications, and supervisor ratings.</p>
            </div>

            {/* Card 2: 100% Statutory Compliance */}
            <div className="career-why-card 3d-card">
              <div className="why-card-glow" />
              <div className="career-why-icon-3d icon-benefits">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                  <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                </svg>
              </div>
              <span className="why-card-badge">Govt Compliant</span>
              <h4>Statutory Benefits</h4>
              <p>Guaranteed on-time 7th-of-month salary deposit, PF & ESIC healthcare benefits, and transparent pay slips.</p>
            </div>

            {/* Card 3: Supportive Field Ecosystem */}
            <div className="career-why-card 3d-card">
              <div className="why-card-glow" />
              <div className="career-why-icon-3d icon-support">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <span className="why-card-badge">People First</span>
              <h4>Supportive Culture</h4>
              <p>Direct access to area field officers, dedicated grievance redressal cell, and uniform allowance.</p>
            </div>

            {/* Card 4: Long-Term Job Security */}
            <div className="career-why-card 3d-card">
              <div className="why-card-glow" />
              <div className="career-why-icon-3d icon-stability">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
              </div>
              <span className="why-card-badge">Verified Sites</span>
              <h4>Job Stability</h4>
              <p>Multi-year contracts with top government departments, state ICT labs, banks, and tier-1 corporate entities.</p>
            </div>
          </div>
        </div>
      </section>
    </section>
  );
};

export default Career;